import { createClient, type RealtimeChannel, type SupabaseClient } from '@supabase/supabase-js';
import type { PlayerId } from './roster';
import { PLAYER_IDS } from './roster';
import { loadActivePlayer, saveActivePlayer } from './localStore';
import { isLocked } from './lock';
import { clearUnlockedSheet, unlockedGameIds } from './resetSheet';
import type { Game, PickStore, WeekSheet, WeekState } from './types';
import { findTiebreakerGame } from './scoring';
import { mergeWeekGames } from './espnIntel';

interface PickRow {
  week_key: string;
  player_id: string;
  game_id: string;
  team_id: string;
}

interface SheetRow {
  week_key: string;
  player_id: string;
  tiebreaker: number | null;
}

function emptySheet(playerId: PlayerId, weekKey: string): WeekSheet {
  return { playerId, weekKey, picks: {} };
}

function emptySheets(weekKey: string): Record<PlayerId, WeekSheet> {
  const sheets = {} as Record<PlayerId, WeekSheet>;
  for (const id of PLAYER_IDS) {
    sheets[id] = emptySheet(id, weekKey);
  }
  return sheets;
}

export function sheetsFromDbRows(
  weekKey: string,
  pickRows: PickRow[],
  sheetRows: SheetRow[],
): Record<PlayerId, WeekSheet> {
  const sheets = emptySheets(weekKey);
  for (const row of sheetRows) {
    const pid = row.player_id as PlayerId;
    if (!PLAYER_IDS.includes(pid)) continue;
    sheets[pid].tiebreaker = row.tiebreaker ?? undefined;
  }
  for (const row of pickRows) {
    const pid = row.player_id as PlayerId;
    if (!PLAYER_IDS.includes(pid)) continue;
    if (!row.team_id) continue;
    sheets[pid].picks[row.game_id] = row.team_id;
  }
  return sheets;
}

export async function loadSheetsForWeek(
  client: SupabaseClient,
  weekKey: string,
): Promise<Record<PlayerId, WeekSheet>> {
  const [pickRes, sheetRes] = await Promise.all([
    client.from('picks').select('*').eq('week_key', weekKey),
    client.from('week_sheets').select('*').eq('week_key', weekKey),
  ]);
  return sheetsFromDbRows(
    weekKey,
    (pickRes.data ?? []) as PickRow[],
    (sheetRes.data ?? []) as SheetRow[],
  );
}

export async function listPickWeekKeys(client: SupabaseClient): Promise<string[]> {
  const { data, error } = await client.from('picks').select('week_key');
  if (error || !data) return [];
  const keys = new Set<string>();
  for (const row of data) {
    if (row.week_key) keys.add(row.week_key);
  }
  return [...keys];
}

/** Season bonus points by player. Fail open: a missing table or any error means none. */
export async function loadSeasonAdjustments(
  client: SupabaseClient,
): Promise<Partial<Record<PlayerId, number>>> {
  try {
    const { data, error } = await client.from('season_adjustments').select('player_id, points');
    if (error || !data) return {};
    const out: Partial<Record<PlayerId, number>> = {};
    for (const row of data as { player_id: string; points: number }[]) {
      const pid = row.player_id as PlayerId;
      if (PLAYER_IDS.includes(pid) && Number.isFinite(row.points)) out[pid] = row.points;
    }
    return out;
  } catch {
    return {};
  }
}

export function createSupabaseStore(
  client: SupabaseClient,
  initialWeekKey: string,
  dateRange: string,
  games: Game[],
): PickStore {
  let weekKey = initialWeekKey;
  let state: WeekState = {
    weekKey,
    dateRange,
    games,
    sheets: emptySheets(weekKey),
    tiebreakerGameId: findTiebreakerGame(games)?.id ?? null,
  };
  let activePlayer: PlayerId = loadActivePlayer();
  const listeners = new Set<() => void>();
  let channel: RealtimeChannel | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  function emit(): void {
    for (const fn of listeners) fn();
  }

  async function loadFromDb(): Promise<void> {
    const sheets = await loadSheetsForWeek(client, weekKey);

    state = {
      ...state,
      sheets,
      tiebreakerGameId: findTiebreakerGame(state.games)?.id ?? null,
    };
    emit();
  }

  function bindChannel(): void {
    if (channel) void client.removeChannel(channel);
    channel = client
      .channel(`pickem-${weekKey}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'picks', filter: `week_key=eq.${weekKey}` },
        () => void loadFromDb(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'week_sheets', filter: `week_key=eq.${weekKey}` },
        () => void loadFromDb(),
      )
      .subscribe();
  }

  return {
    async init() {
      await loadFromDb();
      bindChannel();

      pollTimer = setInterval(() => void loadFromDb(), 5000);
    },

    getWeek() {
      return state;
    },

    setActivePlayer(playerId) {
      activePlayer = playerId;
      saveActivePlayer(playerId);
      emit();
    },

    getActivePlayer() {
      return activePlayer;
    },

    async setPick(gameId, teamId) {
      const game = state.games.find((g) => g.id === gameId);
      if (game && isLocked(game.kickoffUtc)) return;
      const { error } = await client.from('picks').upsert(
        {
          week_key: weekKey,
          player_id: activePlayer,
          game_id: gameId,
          team_id: teamId,
        },
        { onConflict: 'week_key,player_id,game_id' },
      );
      if (error) throw error;
      state.sheets[activePlayer].picks[gameId] = teamId;
      emit();
    },

    async setTiebreaker(value) {
      const tb = state.games.find((g) => g.id === state.tiebreakerGameId);
      if (tb && isLocked(tb.kickoffUtc)) return;
      const { error } = await client.from('week_sheets').upsert(
        {
          week_key: weekKey,
          player_id: activePlayer,
          tiebreaker: value ?? null,
        },
        { onConflict: 'week_key,player_id' },
      );
      if (error) throw error;
      state.sheets[activePlayer].tiebreaker = value;
      emit();
    },

    async resetActiveSheet() {
      const toClear = unlockedGameIds(state.games).filter(
        (id) => state.sheets[activePlayer].picks[id],
      );
      if (toClear.length > 0) {
        const { error } = await client
          .from('picks')
          .delete()
          .eq('week_key', weekKey)
          .eq('player_id', activePlayer)
          .in('game_id', toClear);
        if (error) {
          const fallback = await client
            .from('picks')
            .update({ team_id: '' })
            .eq('week_key', weekKey)
            .eq('player_id', activePlayer)
            .in('game_id', toClear);
          if (fallback.error) throw fallback.error;
        }
      }
      const tb = state.games.find((g) => g.id === state.tiebreakerGameId);
      if (!tb || !isLocked(tb.kickoffUtc)) {
        const { error } = await client.from('week_sheets').upsert(
          {
            week_key: weekKey,
            player_id: activePlayer,
            tiebreaker: null,
          },
          { onConflict: 'week_key,player_id' },
        );
        if (error) throw error;
      }
      state.sheets[activePlayer] = clearUnlockedSheet(
        state.sheets[activePlayer],
        state.games,
        state.tiebreakerGameId,
      );
      emit();
    },

    async refreshGames(newGames) {
      const games = mergeWeekGames(state.games, newGames);
      state = {
        ...state,
        games,
        tiebreakerGameId: findTiebreakerGame(games)?.id ?? null,
        gamesSnapshotAt: new Date().toISOString(),
      };
      emit();
    },

    async replaceWeek(next) {
      weekKey = next.weekKey;
      state = {
        weekKey,
        dateRange: next.dateRange,
        games: next.games,
        sheets: emptySheets(weekKey),
        tiebreakerGameId: findTiebreakerGame(next.games)?.id ?? null,
      };
      emit();
      bindChannel();
      await loadFromDb();
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          if (channel) void client.removeChannel(channel);
          if (pollTimer) clearInterval(pollTimer);
        }
      };
    },
  };
}

export function getSupabaseClient(): SupabaseClient | null {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!url || !key) return null;
  return createClient(url, key);
}
